import { AppShell, Banner, Button, ConnectionDot, type ConnectionState } from "@omony/ui";
import { Lock, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { logout } from "../shared/auth/screens.js";
import { navigate, useHashRoute } from "../shared/hashRoute.js";
import type { RealtimeEvent } from "../shared/realtime.js";
import { chime } from "../shared/sound.js";
import { useVersionWatcher } from "../shared/useVersionWatcher.js";
import { BillingOverview } from "./account/BillingOverview.js";
import { StaffAdmin } from "./account/StaffAdmin.js";
import styles from "./console.module.css";
import { CaseFlow } from "./flow/CaseFlow.js";
import { NewCase } from "./flow/NewCase.js";
import { Home } from "./Home.js";
import { ConsoleContext } from "./context.js";
import { useWakeLock } from "./hooks.js";

interface Alert {
  key: string;
  kind: "vitals" | "staff" | "queue" | "videoEnded";
  caseId: string | null;
}

/** Hinweise aus der Echtzeit-Verbindung (Vitalwert-Anforderung, Personal gerufen, lange Wartezeit, Gesprächsende) */
function toAlert(e: RealtimeEvent): Alert | null {
  const caseId = typeof e.data.caseId === "string" ? e.data.caseId : null;
  const kind =
    e.type === "vitals.requested"
      ? "vitals"
      : e.type === "staff.called"
        ? "staff"
        : e.type === "queue.warn"
          ? "queue"
          : e.type === "video.ended"
            ? "videoEnded"
            : null;
  return kind ? { key: `${e.id ?? Date.now()}-${kind}`, kind, caseId } : null;
}

export function Console({
  mode,
  userName,
  pharmacyName,
  connection,
  events = [],
  onLock,
}: {
  mode: "staff" | "account";
  userName: string;
  pharmacyName?: string;
  connection: ConnectionState;
  events?: RealtimeEvent[];
  onLock?: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const [route] = useHashRoute();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const seen = useRef(new Set<string>());

  // SmED-Rücksprung (Redirect-Variante): /?resume=<vorgang> → Vorgang wieder öffnen
  useEffect(() => {
    const resume = new URLSearchParams(location.search).get("resume");
    if (resume && /^[0-9a-f-]{36}$/.test(resume)) {
      // Parameter entfernen, dann per Hash navigieren (löst hashchange aus)
      history.replaceState(null, "", "/");
      navigate(`vorgang/${resume}`);
    }
  }, []);

  useEffect(() => {
    for (const e of events) {
      const a = toAlert(e);
      if (!a || seen.current.has(a.key)) continue;
      seen.current.add(a.key);
      setAlerts((l) => [...l.filter((x) => !(x.kind === a.kind && x.caseId === a.caseId)), a]);
      chime(a.kind === "staff" || a.kind === "vitals" ? "urgent" : "info");
    }
  }, [events]);

  const section = route[0] ?? "";
  const inCase = section === "vorgang" && !!route[1];
  useWakeLock(inCase);
  const update = useVersionWatcher(() => section === "");
  useEffect(() => {
    document.title = `${t("app.apotheke")} – ${t("app.name")}`;
  });

  let page;
  if (section === "neu") page = <NewCase />;
  else if (inCase) page = <CaseFlow caseId={route[1]!} />;
  else if (section === "abrechnung" && mode === "account") page = <BillingOverview />;
  else if (section === "mitarbeitende" && mode === "account") page = <StaffAdmin />;
  else page = <Home mode={mode} />;

  return (
    <ConsoleContext.Provider value={{ mode, lock: onLock ?? null }}>
      <AppShell
        appTitle={t("app.apotheke")}
        onHome={section ? () => navigate("") : undefined}
        context={
          <span className={styles.ctx}>
            <span className={styles.ctxText}>
              {pharmacyName ? <b>{pharmacyName}</b> : null}
              <span>{userName}</span>
            </span>
            <ConnectionDot state={connection} />
            {onLock ? (
              <Button variant="quiet" className={styles.ctxButton} icon={<Lock />} onPress={onLock}>
                {t("console.lock.lockNow")}
              </Button>
            ) : (
              <Button
                variant="quiet"
                className={styles.ctxButton}
                icon={<LogOut />}
                onPress={() => void logout()}
              >
                {t("auth.logout")}
              </Button>
            )}
          </span>
        }
        banner={
          <>
            {update ? <Banner tone="info">{t("update.available")}</Banner> : null}
            {alerts.length ? (
              <div className={styles.alerts}>
                {alerts.map((a) => (
                  <Banner
                    key={a.key}
                    tone={a.kind === "staff" || a.kind === "vitals" ? "warning" : "info"}
                    actions={
                      <>
                        {a.caseId ? (
                          <Button
                            size="md"
                            onPress={() => {
                              navigate(`vorgang/${a.caseId}`);
                              setAlerts((l) => l.filter((x) => x.key !== a.key));
                            }}
                          >
                            {t("console.alert.open")}
                          </Button>
                        ) : null}
                        <Button
                          variant="quiet"
                          onPress={() => setAlerts((l) => l.filter((x) => x.key !== a.key))}
                        >
                          {t("common.close")}
                        </Button>
                      </>
                    }
                  >
                    {t(`console.alert.${a.kind}`)}
                  </Banner>
                ))}
              </div>
            ) : null}
          </>
        }
      >
        {page}
      </AppShell>
    </ConsoleContext.Provider>
  );
}
